import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Check, Zap, Crown, ArrowRight, LogIn, Sparkles, Building2, MessageSquare, Tag } from 'lucide-react';
import GlobalNav from '@/components/GlobalNav';
import { ContactButton } from '@/components/ContactForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default tiers - Core, Pro, and Enterprise
const DEFAULT_TIERS = {
  core: {
    id: 'core',
    name: 'Core',
    price: 19,
    period: '/mo',
    description: 'For solo founders & small businesses',
    features: [
      '30 credits/month',
      'Unlimited page depth',
      '1 monitored domain',
      'Full report + Action Plan',
      'PDF export',
      'Email support (24-48h)'
    ],
    cta: 'Choose Core',
    highlight: false,
    icon: Zap
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 29,
    period: '/mo',
    description: 'Comprehensive analysis with competitors',
    features: [
      '90 credits/month',
      'Unlimited page depth',
      'Up to 3 monitored domains',
      '3 competitors per audit',
      'Full report + Action Plan',
      'PDF export',
      'Competitor analysis',
      'Priority support (12h)'
    ],
    cta: 'Choose Pro',
    highlight: false,
    popular: false,
    icon: Crown
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: null,
    period: '',
    description: 'For agencies & developers with custom requirements',
    features: [
      'Unlimited audits',
      'Unlimited domains',
      'Unlimited page depth',
      'Custom credit allocation',
      'White-label options',
      'Priority support',
      'Custom integrations',
      'Dedicated account manager'
    ],
    cta: 'Contact Us',
    highlight: false,
    isCustom: true,
    icon: Building2
  }
};

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [currentTier, setCurrentTier] = useState('free');
  const [tiers, setTiers] = useState([DEFAULT_TIERS.core, DEFAULT_TIERS.pro, DEFAULT_TIERS.enterprise]);
  const [enterprisePricing, setEnterprisePricing] = useState(null);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  useEffect(() => {
    fetchDynamicPricing();
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const fetchDynamicPricing = async () => {
    try {
      const headers = {};
      // Include auth token if available for enterprise custom pricing
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await axios.get(`${API}/pricing/dynamic`, { headers });
      
      // Check for user's custom enterprise pricing
      if (res.data.user_enterprise_pricing) {
        setEnterprisePricing(res.data.user_enterprise_pricing);
      }
      
      if (res.data.tiers && res.data.tiers.length > 0) {
        const dynamicTiers = [];
        let hasEnterprise = false;
        
        res.data.tiers.forEach(dt => {
          // Handle both 'entry' and 'core' from API
          const tierId = dt.id === 'entry' ? 'core' : dt.id;
          
          if (tierId === 'core' || tierId === 'pro') {
            const base = DEFAULT_TIERS[tierId];
            if (base) {
              const useFeatures = dt.features && dt.features.length >= 3 ? dt.features : base.features;
              dynamicTiers.push({
                ...base,
                price: dt.price,
                original_price: dt.original_price,
                features: useFeatures,
                special_offer: dt.special_offer,
                special_offer_text: dt.special_offer_text,
                discount_percent: dt.discount_percent,
                popular: dt.popular || false,
                highlight: dt.popular || false
              });
            }
          } else if (tierId === 'enterprise') {
            // Use enterprise features from database if available
            hasEnterprise = true;
            const base = DEFAULT_TIERS.enterprise;
            const useFeatures = dt.features && dt.features.length >= 3 ? dt.features : base.features;
            dynamicTiers.push({
              ...base,
              features: useFeatures,
              description: dt.description || base.description,
              popular: dt.popular || false
            });
          }
        });
        
        // Sort: core, pro, enterprise
        dynamicTiers.sort((a, b) => {
          const order = { core: 0, pro: 1, enterprise: 2 };
          return (order[a.id] ?? 99) - (order[b.id] ?? 99);
        });
        
        // Add enterprise at the end if not present in API response
        if (!hasEnterprise) {
          dynamicTiers.push(DEFAULT_TIERS.enterprise);
        }
        
        if (dynamicTiers.length > 0) {
          setTiers(dynamicTiers);
        }
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/subscription`);
      const tier = res.data.tier === 'entry' ? 'core' : res.data.tier;
      const status = res.data.subscription_status;
      
      // Enterprise with pending_payment should NOT show as current plan
      // User must pay first
      if (tier === 'enterprise' && status === 'pending_payment') {
        setCurrentTier('free'); // Show as if no plan yet - needs payment
        if (res.data.enterprise_config) {
          setEnterprisePricing(res.data.enterprise_config);
        }
      } else {
        setCurrentTier(tier || 'free');
        // If enterprise with active status, get custom pricing
        if (tier === 'enterprise' && res.data.enterprise_config) {
          setEnterprisePricing(res.data.enterprise_config);
        }
      }
    } catch (err) {
      // Ignore
    }
  };

  const validateDiscountCode = async (tier = null) => {
    if (!discountCode.trim()) return;
    setValidatingCode(true);
    try {
      const res = await axios.post(`${API}/discount-codes/validate`, {
        code: discountCode.trim(),
        tier: tier || 'pro' // Default to pro for general validation
      });
      setDiscountInfo(res.data);
      toast.success(`Code "${res.data.code}" applied! ${res.data.discount_type === 'percent' ? res.data.discount_value + '% off' : '£' + res.data.discount_value + ' off'} your first month`);
    } catch (err) {
      setDiscountInfo(null);
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setValidatingCode(false);
    }
  };

  const handleSelectTier = async (tierId) => {
    if (!user) {
      toast.error('You must log in to purchase a plan');
      navigate('/login');
      return;
    }

    if (tierId === currentTier) {
      toast.info('You are already on this plan');
      return;
    }

    // Handle upgrade from current paid tier
    if (currentTier !== 'free' && currentTier !== tierId) {
      toast.info('To change plans, please manage your subscription in the dashboard.');
      navigate('/dashboard');
      return;
    }

    setLoading(tierId);
    try {
      // First validate discount code if present
      if (discountCode.trim()) {
        await validateDiscountCode(tierId);
      }
      
      const res = await axios.post(`${API}/checkout/create`, { 
        tier: tierId,
        origin_url: window.location.origin,
        discount_code: discountCode.trim() || undefined
      });
      // Redirect to Stripe
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setLoading(null);
    }
  };

  // Feature comparison table data
  const featureComparison = [
    { feature: 'Credits per month', core: '30', pro: '90', enterprise: 'Custom' },
    { feature: 'Scan depth', core: 'Unlimited', pro: 'Unlimited', enterprise: 'Unlimited' },
    { feature: 'Monitored domains', core: '1', pro: '3', enterprise: 'Custom' },
    { feature: 'Competitors per audit', core: '-', pro: '3', enterprise: 'Custom' },
    { feature: 'Full report', core: true, pro: true, enterprise: true },
    { feature: 'Action plan', core: true, pro: true, enterprise: true },
    { feature: 'PDF export', core: true, pro: true, enterprise: true },
    { feature: 'White-label options', core: false, pro: false, enterprise: true },
    { feature: 'Priority support', core: false, pro: true, enterprise: true },
    { feature: 'Custom integrations', core: false, pro: false, enterprise: true },
  ];

  return (
    <>
      <GlobalNav />
      <Helmet>
        <title>ScoreCite Pricing – AI Site Audit Plans</title>
        <meta name="description" content="Choose the right ScoreCite plan for your needs. Core for solo founders, Pro for comprehensive analysis, Enterprise for agencies with custom requirements." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] py-16 px-4" data-testid="pricing-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 border-blue-500/50 text-blue-400">
              Pricing
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple, transparent pricing
            </h1>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Start with a free audit. Upgrade when you need more power.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 items-stretch">
            {tiers.map((tier) => {
              const Icon = tier.icon;
              const isCurrentPlan = currentTier === tier.id;
              const isEnterprise = tier.id === 'enterprise';
              const hasCustomPrice = isEnterprise && enterprisePricing?.custom_price;
              
              return (
                <Card 
                  key={tier.id}
                  className={`bg-[#121212] border relative flex flex-col h-full ${
                    tier.highlight 
                      ? 'border-blue-500 shadow-lg shadow-blue-500/10' 
                      : isEnterprise 
                        ? 'border-purple-500/50'
                        : 'border-white/10'
                  }`}
                  data-testid={`pricing-card-${tier.id}`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-blue-600 text-white">Most Popular</Badge>
                    </div>
                  )}
                  
                  {tier.special_offer && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-green-600 text-white flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> {tier.special_offer_text || 'Special Offer'}
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${
                        tier.highlight ? 'bg-blue-500/20' : isEnterprise ? 'bg-purple-500/20' : 'bg-zinc-800'
                      }`}>
                        <Icon className={`w-4 h-4 ${
                          tier.highlight ? 'text-blue-400' : isEnterprise ? 'text-purple-400' : 'text-zinc-400'
                        }`} />
                      </div>
                      <CardTitle className="text-white text-lg">{tier.name}</CardTitle>
                    </div>
                    
                    {/* Price */}
                    <div className="flex items-baseline gap-1 mb-2">
                      {isEnterprise && !hasCustomPrice ? (
                        <span className="text-2xl font-bold text-purple-400">Custom Pricing</span>
                      ) : (
                        <>
                          {tier.original_price && tier.original_price !== tier.price && (
                            <span className="text-xl text-zinc-500 line-through mr-2">£{tier.original_price}</span>
                          )}
                          <span className="text-4xl font-bold text-white">
                            £{hasCustomPrice ? enterprisePricing.custom_price : tier.price}
                          </span>
                          <span className="text-zinc-500">{tier.period || '/mo'}</span>
                        </>
                      )}
                    </div>
                    
                    <p className="text-zinc-400 text-sm">{tier.description}</p>
                    
                    {/* Custom enterprise info */}
                    {isEnterprise && enterprisePricing?.first_month_discount > 0 && (
                      <div className="mt-2 p-2 bg-green-500/10 rounded-sm border border-green-500/20">
                        <p className="text-green-400 text-sm font-semibold">
                          🎉 {enterprisePricing.first_month_discount}% off first month!
                        </p>
                      </div>
                    )}
                  </CardHeader>

                  <CardContent className="pt-4 flex-grow flex flex-col">
                    {/* Features */}
                    <ul className="space-y-3 flex-grow">
                      {/* For Enterprise with custom pricing, show offer details */}
                      {isEnterprise && enterprisePricing?.custom_price ? (
                        <>
                          <li className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-400" />
                            <span className="text-zinc-300">{enterprisePricing.custom_credits || 'Custom'} credits per month</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-400" />
                            <span className="text-zinc-300">{enterprisePricing.custom_domains || 'Unlimited'} domains</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-400" />
                            <span className="text-zinc-300">Full AI-powered audits</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-400" />
                            <span className="text-zinc-300">Priority support</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-400" />
                            <span className="text-zinc-300">White-label options</span>
                          </li>
                        </>
                      ) : (
                        tier.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                              tier.highlight ? 'text-blue-400' : isEnterprise ? 'text-purple-400' : 'text-green-400'
                            }`} />
                            <span className="text-zinc-300">{feature}</span>
                          </li>
                        ))
                      )}
                    </ul>

                    {/* CTA Button - always at bottom */}
                    <div className="mt-6">
                    {isCurrentPlan ? (
                      <Button disabled className="w-full bg-zinc-700 text-zinc-400">
                        Current Plan
                      </Button>
                    ) : isEnterprise ? (
                      hasCustomPrice ? (
                        <>
                          {enterprisePricing.first_month_discount > 0 && (
                            <p className="text-center text-green-400 text-xs mb-2">
                              First month: £{Math.round(enterprisePricing.custom_price * (1 - enterprisePricing.first_month_discount / 100))}, then £{enterprisePricing.custom_price}/mo
                            </p>
                          )}
                          <Button 
                            onClick={() => handleSelectTier('enterprise')}
                            disabled={loading === 'enterprise'}
                            className="w-full bg-purple-600 hover:bg-purple-500 text-white"
                            data-testid="select-tier-enterprise"
                          >
                            {loading === 'enterprise' ? 'Processing...' : enterprisePricing.first_month_discount > 0 
                              ? `Pay £${Math.round(enterprisePricing.custom_price * (1 - enterprisePricing.first_month_discount / 100))} now`
                              : `Pay £${enterprisePricing.custom_price}/mo`}
                            {loading !== 'enterprise' && <ArrowRight className="w-4 h-4 ml-2" />}
                          </Button>
                        </>
                      ) : (
                        <ContactButton 
                          className="w-full bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/50 text-white"
                          buttonText="Contact Us"
                        />
                      )
                    ) : user ? (
                      <Button 
                        onClick={() => handleSelectTier(tier.id)}
                        disabled={loading === tier.id}
                        className={`w-full ${
                          tier.highlight 
                            ? 'bg-blue-600 hover:bg-blue-500' 
                            : 'bg-zinc-700 hover:bg-zinc-600'
                        }`}
                        data-testid={`select-tier-${tier.id}`}
                      >
                        {loading === tier.id ? 'Processing...' : tier.cta}
                        {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                      </Button>
                    ) : (
                      <Link to="/login">
                        <Button className={`w-full ${
                          tier.highlight 
                            ? 'bg-blue-600 hover:bg-blue-500' 
                            : 'bg-zinc-700 hover:bg-zinc-600'
                        }`}>
                          <LogIn className="w-4 h-4 mr-2" /> Login to Subscribe
                        </Button>
                      </Link>
                    )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Discount Code Section */}
          <div className="max-w-md mx-auto mb-12">
            <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Have a discount code?
              </h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  className="bg-black border-white/20 text-white placeholder-zinc-600"
                  data-testid="discount-code-input"
                />
                <Button
                  onClick={() => validateDiscountCode()}
                  disabled={validatingCode || !discountCode.trim()}
                  className="bg-blue-600 hover:bg-blue-500 whitespace-nowrap"
                  data-testid="apply-discount-btn"
                >
                  {validatingCode ? 'Checking...' : 'Apply'}
                </Button>
              </div>
              {discountInfo && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded text-sm">
                  <p className="text-green-400 font-medium">
                    ✓ Code "{discountInfo.code}" applied!
                  </p>
                  <p className="text-green-300">
                    {discountInfo.discount_type === 'percent' 
                      ? `${discountInfo.discount_value}% off` 
                      : `£${discountInfo.discount_value} off`} your first payment
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Feature Comparison Table */}
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-semibold text-white">Feature Comparison</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-zinc-400 font-medium">Feature</th>
                    <th className="text-center p-4 text-zinc-400 font-medium">Core</th>
                    <th className="text-center p-4 text-blue-400 font-medium">Pro</th>
                    <th className="text-center p-4 text-purple-400 font-medium">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {featureComparison.map((row, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-4 text-zinc-300">{row.feature}</td>
                      <td className="p-4 text-center">
                        {typeof row.core === 'boolean' ? (
                          row.core ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <span className="text-zinc-600">-</span>
                        ) : (
                          <span className="text-zinc-300">{row.core}</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {typeof row.pro === 'boolean' ? (
                          row.pro ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <span className="text-zinc-600">-</span>
                        ) : (
                          <span className="text-zinc-300">{row.pro}</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {typeof row.enterprise === 'boolean' ? (
                          row.enterprise ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <span className="text-zinc-600">-</span>
                        ) : (
                          <span className="text-purple-300">{row.enterprise}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ Link */}
          <div className="text-center mt-12">
            <p className="text-zinc-400 mb-4">Have questions?</p>
            <Link to="/faq">
              <Button variant="outline" className="border-white/20 text-zinc-300 hover:text-white">
                View FAQ
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
